import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalTokenProviderService } from '@services/msal-token-provider.service';
import { ROLE_ADMIN, ROLE_AUTHOR, ROUTES } from '@shared/config';
import {
  OrCheckLoginModel,
  OrLoginModel,
} from '@shared/models/login/or-login.model';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { OrUserSettingsService } from '@shared/services/or-user-settings.service';
import { ToastrService } from '@shared/services/toaster.service';
import { from, Observable, of } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  getIdTokenFromFlutterWebViewEvent,
  isInFlutterWrapper,
  logoutFromFlutterWebViewEvent,
} from '@security/flutter-authentication.utils';
import {
  LOGIN_ERROR_QUERY_PARAM_NAME,
  LoginErrorType,
} from 'src/app/should-login/should-login.config';
import { AuthenticationService } from '@security/authentication.service';
import { MsalAuthenticationService } from '@security/msal-authentication.service';
import { isString } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly userSettingsService = inject(OrUserSettingsService);
  private readonly toastr = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly msalAuthService = inject(MsalAuthenticationService);
  private readonly msalTokenProvider = inject(MsalTokenProviderService);

  private cached: { value: boolean; at: number } | null = null;
  private inFlight$: Observable<boolean> | null = null;
  private readonly TTL_MS = 3000;

  /**
   * Resolves whether there is a usable authenticated session (and, when MSAL is
   * enabled, a hydrated one).
   */
  public resolve(): Observable<boolean> {
    const cached = this.readCache();
    if (cached !== null) {
      return of(cached);
    }

    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = this.resolveSession().pipe(
      tap(value => {
        this.writeCache(value);
        this.inFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.inFlight$;
  }

  private readCache(): boolean | null {
    if (
      this.cached &&
      Date.now() - this.cached.at < this.TTL_MS &&
      this.cached.value === LocalStorageService.isLoggedIn()
    ) {
      return this.cached.value;
    }
    return null;
  }

  private writeCache(value: boolean): void {
    this.cached = { value, at: Date.now() };
  }

  private resolveSession(): Observable<boolean> {
    return this.authenticationService.status().pipe(
      switchMap(status => {
        if (status.isLoggedIn) {
          return this.handleLoggedIn(status);
        }
        return this.tryRecoverSession();
      }),
      catchError(() => of(false))
    );
  }

  private handleLoggedIn(status: OrCheckLoginModel): Observable<boolean> {
    if (status.userLoginDetails) {
      this.populateSession(status.userLoginDetails);
    }
    return this.ensureMsalHydratedWhenInternalSession$();
  }

  private tryRecoverSession(): Observable<boolean> {
    return this.authenticationService.refreshToken().pipe(
      switchMap(() => this.authenticationService.status()),
      switchMap(refreshStatus => {
        if (refreshStatus.isLoggedIn) {
          return this.handleLoggedIn(refreshStatus);
        }
        return this.tryMsalAuthentication();
      }),
      catchError(() => this.tryMsalAuthentication())
    );
  }

  private tryMsalAuthentication(): Observable<boolean> {
    if (!this.msalAuthService.isMsalAuthenticationEnabled()) {
      return of(false);
    }

    if (isInFlutterWrapper()) {
      return from(getIdTokenFromFlutterWebViewEvent()).pipe(
        switchMap(idToken => this.authenticateOnce(idToken)),
        catchError(() => of(false))
      );
    }

    return this.msalAuthService.ensureSession().pipe(
      switchMap(silentResult => {
        if (silentResult?.idToken) {
          return this.authenticateOnce(silentResult.idToken);
        }

        return this.tryDeviceSessionAuthentication({
          fallbackToMsal: !this.msalAuthService.hasSyntheticAccount(),
        });
      }),
      catchError(err => {
        this.toastr.error(this.getError(err));
        return of(false);
      })
    );
  }

  private tryDeviceSessionAuthentication({
    fallbackToMsal,
  }: {
    fallbackToMsal: boolean;
  }): Observable<boolean> {
    return this.msalTokenProvider.getIdTokenFromDeviceSession().pipe(
      switchMap(idToken => {
        if (idToken) {
          return this.authenticateOnce(idToken);
        }
        return fallbackToMsal
          ? this.acquireMsalTokenAndAuthenticate()
          : of(false);
      }),
      catchError(() =>
        fallbackToMsal ? this.acquireMsalTokenAndAuthenticate() : of(false)
      )
    );
  }

  private acquireMsalTokenAndAuthenticate(): Observable<boolean> {
    return this.msalAuthService
      .authenticateFromWebBrowser({
        allowInteraction: false,
        forceRefresh: false,
      })
      .pipe(
        switchMap(authResult => {
          if (authResult?.idToken) {
            return this.authenticateOnce(authResult.idToken);
          }

          return this.msalAuthService.authenticateFromWebBrowser({
            allowInteraction: false,
            forceRefresh: true,
          });
        }),
        switchMap(result => {
          if (result === true) {
            return of(true);
          }

          const idToken = typeof result === 'object' ? result?.idToken : null;
          if (idToken) {
            return this.authenticateOnce(idToken);
          }

          return of(false);
        })
      );
  }

  private authenticateOnce(idToken: string): Observable<boolean> {
    if (!idToken) {
      return of(false);
    }

    return this.authenticationService.authenticate(idToken).pipe(
      map(response => {
        this.populateSession(response);
        return true;
      }),
      catchError(() => of(false))
    );
  }

  private ensureMsalHydratedWhenInternalSession$(): Observable<boolean> {
    if (!this.msalAuthService.isMsalAuthenticationEnabled()) {
      return of(true);
    }

    return this.msalAuthService.ensureSession().pipe(
      switchMap(result => {
        if (result || this.msalAuthService.isSessionActive()) {
          return of(true);
        }

        return this.msalTokenProvider.getIdTokenFromDeviceSession().pipe(
          switchMap(() => {
            if (this.msalAuthService.isSessionActive()) {
              return of(true);
            }
            return this.cleanOrphanedSession$();
          }),
          catchError(() => this.cleanOrphanedSession$())
        );
      }),
      catchError(() => this.cleanOrphanedSession$())
    );
  }

  /**
   * Internal session exists but MSAL could not be hydrated: tear down the
   * orphaned server session and report the session as not authenticated.
   */
  private cleanOrphanedSession$(): Observable<boolean> {
    return this.authenticationService.logout().pipe(
      catchError(() => of(void 0)),
      tap(() => {
        this.msalAuthService.setSessionAccount(null);
        LocalStorageService.cleanCookies();
      }),
      map(() => false)
    );
  }

  private getError(error: any): string {
    if (error?.error) {
      return isString(error.error) ? error.error : error.error.message;
    }
    return error?.message ?? 'Unknown error';
  }

  public populateSession(loginInfo: OrLoginModel): void {
    LocalStorageService.setAuthorities(loginInfo.authorities);
    LocalStorageService.setUserEmail(loginInfo.userEmail);
    LocalStorageService.setGroups(loginInfo.groups);
    LocalStorageService.setApplications(loginInfo.applications);
    LocalStorageService.login();
  }

  public logout(logoutFromAzure: boolean = false) {
    this.authenticationService
      .logout()
      .pipe(
        finalize(() => {
          LocalStorageService.cleanCookies();
          this.userSettingsService.unsetUserSettings();
          this.toastr.info('You logged out successfully');
          if (!this.msalAuthService.isSessionActive()) {
            this.router.navigate([ROUTES.login]);
            return;
          }
          if (logoutFromAzure) {
            this.logoutFromAzure();
            return;
          }

          this.router.navigate([ROUTES.shouldLogin], {
            queryParams: {
              [LOGIN_ERROR_QUERY_PARAM_NAME]: LoginErrorType.SESSION_EXPIRED,
            },
            replaceUrl: true,
          });
        })
      )
      .subscribe();
  }

  public logoutFromAzure() {
    const logout$ = !isInFlutterWrapper()
      ? this.msalAuthService.logout()
      : from(logoutFromFlutterWebViewEvent()).pipe(map(() => void 0));

    logout$.subscribe(() => {
      this.router.navigate([ROUTES.login]);
    });
  }

  public isAdmin() {
    const roles = LocalStorageService.getAuthorities() || '';
    return roles.includes(ROLE_ADMIN);
  }

  public isAuthor() {
    const roles = LocalStorageService.getAuthorities() || '';
    return roles.includes(ROLE_AUTHOR);
  }
}
